import React, { useMemo, useState } from "react";
import { Row, Col, Spinner, Button } from "react-bootstrap";
import styled from "styled-components";
import AddShoppingCart from "@mui/icons-material/AddShoppingCart";
import CheckIcon from "@mui/icons-material/Check";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import SuccessButton from "../../../organisms/SuccessButton";
import Calendar from "../../../organisms/Calendar";
import { HeaderSection } from "../HeaderSection";
import { Title } from "../Title";
import ServerErrorModal from "../../../organisms/ServerErrorModal";
import { useMutation } from "@tanstack/react-query";
import {
  buildNuevoAgendamiento,
  buildNuevoItemCarritoHotel,
  Model,
} from "../Model";
import CurrencyFormat from "react-currency-format";
import { parseCurrency } from "../../../../utils/numberUtils";
import { agendar } from "../../../../services/agendamientosHotelServicios";
import ExitoCard, { ExitoOption } from "../../../organisms/ExitoCard";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PaymentIcon from "@mui/icons-material/Payment";
import Pagar from "../Pagar/Pagar";
import CalculoPrecioTransporte from "../CalculoPrecioTransporte/CalculoPrecioTransporte";
import Fechas from "../Fechas/Fechas";
import { useWizard } from "../../../../hooks/wizard";
import { obtenerIntervalos } from "../../../../utils/dateUtils";
import InformationModal from "../../../organisms/InformationModal";
import { useCarrito } from "../../../../hooks/carrito";

function Resumen({ model }: { model: Model }) {
  const [currentModel, setCurrentModel] = useState(recalcularTotal(model));
  const { avanza, vuelve, volver, avanzar } = useWizard();
  const {
    preparationOperationsIsLoading: carritoPreparationOperationsIsLoading,
    errorValidacionInclusionCarrito,
    validarInclusion,
    setErrorValidacionInclusionCarrito,
    mutarAgregarAlCarrito,
    agregarAlCarritoIsLoading,
    agregarAlCarritoIsSuccess,
    isError: carritoOperationsIsError,
  } = useCarrito();

  const {
    isError: mutacionAgendarIsError,
    isLoading: mutacionIsLoading,
    isSuccess: agendarIsSuccess,
    mutate,
  } = useMutation(agendar);

  const numeroViajes = useMemo(() => {
    return obtenerIntervalos(currentModel.fechasProgramadas).length;
  }, [currentModel.fechasProgramadas]);

  const viajesLabel =
    numeroViajes > 1 ? `${numeroViajes} viajes x` : `${numeroViajes} viaje x`;

  const error = mutacionAgendarIsError || carritoOperationsIsError;

  const onValorFinalNocheIndividualChange = (
    val: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCurrentModel((prevModel) => {
      const modelActualizado = {
        ...prevModel,
        valorFinalNocheIndividual: parseCurrency(val.target.value, "$", ","),
      };
      return recalcularTotal(modelActualizado);
    });
  };

  const onValorFinalPlanChange = (val: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentModel((prevModel) => {
      const modelActualizado = {
        ...prevModel,
        valorFinalTarifa: parseCurrency(val.target.value, "$", ","),
      };
      return recalcularTotal(modelActualizado);
    });
  };

  const onValorFinalIdaChange = (val: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentModel((prevModel) => {
      const modelActualizado = {
        ...prevModel,
        valorFinalIda: parseCurrency(val.target.value, "$", ","),
      };
      return recalcularTotal(modelActualizado);
    });
  };

  const onValorFinalVueltaChange = (
    val: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCurrentModel((prevModel) => {
      const modelActualizado = {
        ...prevModel,
        valorFinalVuelta: parseCurrency(val.target.value, "$", ","),
      };
      return recalcularTotal(modelActualizado);
    });
  };

  const onValorFinalIdaVueltaChange = (
    val: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCurrentModel((prevModel) => {
      const modelActualizado = {
        ...prevModel,
        valorFinalIdaVuelta: parseCurrency(val.target.value, "$", ","),
      };
      return recalcularTotal(modelActualizado);
    });
  };

  const onDescuentoChange = (val: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentModel((prevModel) => {
      const targetVal: string | undefined = val.target.value;
      const targetValStr: string = targetVal ? targetVal : "$0";
      const modelActualizado = {
        ...prevModel,
        descuento: parseCurrency(targetValStr, "$", ","),
      };
      return recalcularTotal(modelActualizado);
    });
  };

  const onConfirmarClick = () => {
    const agendamiento = buildNuevoAgendamiento(currentModel);
    mutate(agendamiento);
  };

  const onAgregarAlCarritoClick = () => {
    const itemCarrito = buildNuevoItemCarritoHotel(currentModel);
    const isExito = validarInclusion(itemCarrito);

    if (isExito) {
      mutarAgregarAlCarrito(itemCarrito);
    }
  };

  const onModalValidacionInclusionCarritoClose = () => {
    setErrorValidacionInclusionCarrito(undefined);
  };

  if (agregarAlCarritoIsSuccess || agendarIsSuccess) {
    return <ExitoCard title={"Reserva exitosa!"} options={exitoOptions} />;
  }

  if (avanza) {
    return <Pagar model={currentModel} />;
  }

  if (vuelve) {
    if (model.contrataTransporte) {
      return <CalculoPrecioTransporte model={currentModel} />;
    }
    return <Fechas model={currentModel} />;
  }

  return (
    <>
      {errorValidacionInclusionCarrito && (
        <InformationModal
          message={errorValidacionInclusionCarrito.mensaje}
          onClose={onModalValidacionInclusionCarritoClose}
        />
      )}
      {error && <ServerErrorModal />}
      <Row className="justify-content-center">
        <Col className="col-11">
          <HeaderSection className="pt-5 mb-4">
            <Title>
              Resúmen de agendamiento
              <IconContainer>
                <FactCheckIcon sx={{ fontSize: 28 }} />
              </IconContainer>
            </Title>
          </HeaderSection>
          <section className="mb-5">
            <SectionTitle className="mb-3">Datos básicos</SectionTitle>
            <InfoRow
              label="Propietario"
              value={currentModel.nombresPropietario}
            />
            <InfoRow label="Mascota" value={currentModel.nombreMascota} />
            <div>
              <label className="mb-2" style={{ fontWeight: 500 }}>
                Fechas programadas:
              </label>
              <StyledCalendar
                preSelectedDates={currentModel.diasSeleccionados}
                disableAll={true}
              />
            </div>
          </section>

          <section className="mb-5">
            <SectionTitle className="mb-3">Datos de transporte</SectionTitle>
            <InfoRow
              label="Contrata transporte"
              value={!!currentModel.contrataTransporte ? "Si" : "No"}
            />
            {currentModel.contrataTransporte && (
              <>
                <InfoRow
                  label="Sector de transporte"
                  value={currentModel.sectorTransporte?.nombre || ""}
                />
                <InfoRow
                  label="Dirección de transporte"
                  value={currentModel.direccionTransporte || ""}
                />
                <InfoRow
                  label="Ida"
                  value={
                    !!currentModel.contrataIda &&
                    !!currentModel.contrataTransporte
                      ? "Si"
                      : "No"
                  }
                />
                <InfoRow
                  label="Vuelta"
                  value={
                    !!currentModel.contrataVuelta &&
                    !!currentModel.contrataTransporte
                      ? "Si"
                      : "No"
                  }
                />
              </>
            )}
          </section>

          <section className="mb-4">
            <SectionTitle className="mb-3">Tarifas y planes</SectionTitle>
            {currentModel.tarifa && (
              <Row className="mb-2">
                <LabelCol className="col-4">
                  {model.tarifa!!.nombre + ":"}
                </LabelCol>
                <Col className="text-right col-3 p-0">
                  {(currentModel.fechasProgramadas.length - currentModel.numeroNochesIndividuales) + " noches x"}
                </Col>
                <Col className="text-right col-3">
                  <CurrencyFormat
                    name="valorFinalPlan"
                    value={currentModel.valorFinalTarifa}
                    thousandSeparator={","}
                    onChange={onValorFinalPlanChange}
                    prefix={"$"}
                    decimalSeparator={"."}
                  />
                </Col>
              </Row>
            )}
            {currentModel.tarifaNocheIndividual && (
              <Row className="mb-2">
                <LabelCol className="col-4">Noches individuales:</LabelCol>
                <Col className="text-right col-3 p-0">
                  {currentModel.numeroNochesIndividuales + " noches x"}
                </Col>
                <Col className="text-right col-3">
                  <CurrencyFormat
                    name="valorFinalNocheIndividual"
                    value={currentModel.valorFinalNocheIndividual}
                    thousandSeparator={","}
                    onChange={onValorFinalNocheIndividualChange}
                    prefix={"$"}
                    decimalSeparator={"."}
                  />
                </Col>
              </Row>
            )}
            {model.tarifaIda && (
              <Row className="mb-2">
                <LabelCol className="col-4">Transporte ida:</LabelCol>
                <Col className="text-right col-3 p-0">{viajesLabel}</Col>
                <Col className="text-right col-3">
                  <CurrencyFormat
                    name="valorFinalIda"
                    value={currentModel.valorFinalIda}
                    thousandSeparator={","}
                    onChange={onValorFinalIdaChange}
                    prefix={"$"}
                    decimalSeparator={"."}
                  />
                </Col>
              </Row>
            )}
            {model.tarifaVuelta && (
              <Row className="mb-2">
                <LabelCol className="col-4">Transporte vuelta:</LabelCol>
                <Col className="text-right col-3 p-0">{viajesLabel}</Col>
                <Col className="text-right">
                  <CurrencyFormat
                    name="valorFinalVuelta"
                    value={currentModel.valorFinalVuelta}
                    thousandSeparator={","}
                    onChange={onValorFinalVueltaChange}
                    prefix={"$"}
                    decimalSeparator={"."}
                  />
                </Col>
              </Row>
            )}
            {model.tarifaIdaVuelta && (
              <Row className="mb-2">
                <LabelCol className="col-4">Transporte ida y vuelta:</LabelCol>
                <Col className="text-right col-3 p-0">{viajesLabel}</Col>
                <Col className="text-right col-3">
                  <CurrencyFormat
                    name="valorFinalIdaVuelta"
                    value={currentModel.valorFinalIdaVuelta}
                    thousandSeparator={","}
                    onChange={onValorFinalIdaVueltaChange}
                    prefix={"$"}
                    decimalSeparator={"."}
                  />
                </Col>
              </Row>
            )}

            <Row className="mb-2">
              <LabelCol className="col-4">Descuento:</LabelCol>
              <Col className="text-right col-3 p-0"></Col>
              <Col className="text-right col-3">
                <CurrencyFormat
                  name="descuento"
                  value={currentModel.descuento}
                  thousandSeparator={","}
                  onChange={onDescuentoChange}
                  prefix={"$"}
                  decimalSeparator={"."}
                />
              </Col>
            </Row>

            <Row className="mb-2">
              <LabelCol className="col-4">Total: </LabelCol>
              <Col className="col-3 p-0"></Col>
              <Col className="text-right col-3">
                <CurrencyFormat
                  value={currentModel.total}
                  thousandSeparator={","}
                  disabled
                  prefix={"$"}
                  decimalSeparator={"."}
                />
              </Col>
            </Row>
          </section>

          <section className="d-flex justify-content-between">
            <Button onClick={volver}>
              <ArrowBackIcon sx={{ fontSize: 18, marginRight: "2px" }} />
              Volver
            </Button>
            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="primary"
                onClick={onAgregarAlCarritoClick}
                size="sm"
                disabled={carritoPreparationOperationsIsLoading}
              >
                {!agregarAlCarritoIsLoading && (
                  <AddShoppingCart sx={{ fontSize: 18, marginRight: "2px" }} />
                )}
                {agregarAlCarritoIsLoading && (
                  <Spinner animation="border" size="sm" />
                )}
                Agregar
              </Button>
              <Button variant="primary" onClick={avanzar} size="sm">
                <PaymentIcon sx={{ fontSize: 18, marginRight: "2px" }} />
                Pagar
              </Button>
              <SuccessButton
                type={"button"}
                onClick={onConfirmarClick}
                variant="success"
                size="sm"
              >
                {!mutacionIsLoading && (
                  <CheckIcon sx={{ fontSize: 18, marginRight: "2px" }} />
                )}
                {mutacionIsLoading && <Spinner animation="border" size="sm" />}
                Finalizar{" "}
              </SuccessButton>
            </div>
          </section>
        </Col>
      </Row>
    </>
  );
}

const recalcularTotal = (m: Model): Model => {
  const noches = m.fechasProgramadas.length;
  const viajes = obtenerIntervalos(m.fechasProgramadas).length;

  const valorFinalNocheIndividual = m.valorFinalNocheIndividual || 0;
  const valorFinalTarifa = m.valorFinalTarifa || 0;

  const valorFinalIda = (m.valorFinalIda || 0) * viajes;
  const valorFinalVuelta = (m.valorFinalVuelta || 0) * viajes;
  const valorFinalIdaVuelta = (m.valorFinalIdaVuelta || 0) * viajes;

  const subTotalNochesIndividuales = valorFinalNocheIndividual * m.numeroNochesIndividuales;
  const subTotalTarifa = valorFinalTarifa * (noches - m.numeroNochesIndividuales);
  const subTotalTransporte =
    valorFinalIda + valorFinalVuelta + valorFinalIdaVuelta;

  const subTotal =
    subTotalNochesIndividuales + subTotalTarifa + subTotalTransporte;

  return { ...m, total: subTotal - m.descuento };
};

const SectionTitle = styled("h5")`
  font-weight: 500;
`;

const InfoRow = ({ label, value }: { label: string; value?: string }) => {
  return (
    <Row className="mb-2">
      <LabelCol className="col-4">{label + ":"}</LabelCol>
      <Col className="text-right">{value}</Col>
    </Row>
  );
};

const LabelCol = styled(Col)`
  font-weight: 500;
`;

const StyledCalendar = styled(Calendar)`
  width: 100%;
  border-radius: 5px;
  padding: 0%;
`;

const IconContainer = styled("span")`
  display: relative;
  margin-left: 8px;
  color: ${(props) => props.theme.colors.main};
`;

const exitoOptions: ExitoOption[] = [
  {
    description: "Realizar nuevo agendamiento",
    path: "/reservas",
    variant: "primary",
  },
  {
    description: "Ver listado",
    path: "/reservas/hotel/listar",
    variant: "primary",
  },
  {
    description: "Ver carrito",
    path: "/carrito",
    variant: "secondary",
    isLast: true,
  },
];

export default Resumen;
